import { inject, Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { HttpRestService } from '@core/services/http-rest';
import { CartApiResponse } from './cart-item.model';
import { toCartItem } from './cart.mapper';
import {
  GetShoppingCartResponse,
  ShoppingCartItemType,
} from '../../models/shopping-cart.model';
import { toBig } from '@core/utils/big-number.util';
import SHOPPING_CART_ENDPOINTS from './shopping-cart.endpoints';
import {
  CreateBuyRequestRequest,
  CreateBuyRequestByWalletRequest,
} from './shopping-cart.request';
import {
  CreateBuyRequestResponse,
  CreateBuyRequestByWalletResponse,
} from './shopping-cart.response';

@Injectable({ providedIn: 'root' })
export class CartService {
  private readonly _http = inject(HttpRestService);

  getCart(): Observable<CartApiResponse> {
    return this._http
      .get<GetShoppingCartResponse>(SHOPPING_CART_ENDPOINTS.GET)
      .pipe(
        map((response) => {
          const rialCurrencyName = response.rialCurrency?.name ?? '';
          const showCommission = response.showCommission ?? true;
          const rialTotals = new Map<string, string>();

          for (const item of response.items ?? []) {
            if (
              item.itemType === ShoppingCartItemType.GiftCard &&
              item.giftCard
            ) {
              const gc = item.giftCard;
              const total = toBig(gc.amount).times(gc.quantity);
              const rial = total.times(gc.exchangeRateToIrr?.buyRate ?? 0);
              rialTotals.set(item.shoppingCartItemId, rial.toString());
            } else if (
              item.itemType === ShoppingCartItemType.Bundle &&
              item.bundle
            ) {
              const b = item.bundle;
              const rialSum = (b.giftCards ?? []).reduce(
                (sum, g) =>
                  sum.plus(
                    toBig(g.amount).times(g.exchangeRateToIrr?.buyRate ?? 0),
                  ),
                toBig(0),
              );
              rialTotals.set(
                item.shoppingCartItemId,
                rialSum.times(b.quantity).toString(),
              );
            }
          }

          return {
            shoppingCartId: response.shoppingCartId ?? '',
            totalItemCount: response.totalItemCount,
            cartExpiresAt: response.cartExpiresAt,
            token: response.token ?? '',
            expireInSecond: response.expireInSecond ?? 0,
            expireAt: response.expireAt ?? '',
            rialCurrencyName,
            rialTotals,
            showCommission,
            items: (response.items ?? []).map((item) =>
              toCartItem(item, rialCurrencyName, showCommission),
            ),
          };
        }),
      );
  }

  addGiftCard(
    serviceProviderItemId: string,
    amount: string | null,
  ): Observable<void> {
    return this._http.post<void>(
      SHOPPING_CART_ENDPOINTS.ADD_GIFTCARD(serviceProviderItemId),
      { amount },
    );
  }

  addBundle(bundleId: string): Observable<void> {
    return this._http.post<void>(
      SHOPPING_CART_ENDPOINTS.ADD_BUNDLE(bundleId),
      bundleId,
    );
  }

  removeItem(shoppingCartItemId: string): Observable<void> {
    return this._http.delete<void>(
      SHOPPING_CART_ENDPOINTS.DELETE_ITEM(shoppingCartItemId),
      shoppingCartItemId,
    );
  }

  buyCart(
    request: CreateBuyRequestRequest,
  ): Observable<CreateBuyRequestResponse> {
    return this._http.post<CreateBuyRequestResponse>(
      SHOPPING_CART_ENDPOINTS.BUY,
      request,
    );
  }

  buyCartByWallet(
    request: CreateBuyRequestByWalletRequest,
  ): Observable<CreateBuyRequestByWalletResponse> {
    return this._http.post<CreateBuyRequestByWalletResponse>(
      SHOPPING_CART_ENDPOINTS.BUY_BY_WALLET,
      request,
    );
  }
}
